'use client'
import React, { useState, useEffect } from 'react'
import ProgressBar from './ProgressBar'
import classes from './styles.module.css'

const ClientProgressBar = () => {
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  
  useEffect(() => {
    const handleScroll = () => {
      // Get all required elements
      const contentElement = document.getElementById('blog-content')
      const bannerElement = document.getElementById('blog-banner')
      const recommendedElement = document.getElementById('recommended-section')
      
      if (!contentElement || !bannerElement || !recommendedElement) {
        return
      }
      
      // Get the scroll position
      const scrollPosition = window.scrollY
      
      // Get positions of elements
      const bannerBottom = bannerElement.getBoundingClientRect().bottom + window.scrollY
      
      // Find any "call to action" elements that appear at the end of the blog content
      // These might be elements with text like "Book a demo today" or similar
      // We'll use this to determine where to stop showing the progress bar
      const callToActionElements = Array.from(contentElement.querySelectorAll('a[href*="demo"], a[href*="book"]'))
      
      // Get the position of any call-to-action element near the end of the content
      let contentEndPosition = recommendedElement.getBoundingClientRect().top + window.scrollY
      
      // If we found any call-to-action elements, use the position of the last one
      // as our indicator for where the main content ends
      if (callToActionElements.length > 0) {
        // Sort the elements by their position on the page
        callToActionElements.sort((a, b) => {
          const posA = a.getBoundingClientRect().top + window.scrollY
          const posB = b.getBoundingClientRect().top + window.scrollY
          return posA - posB
        })
        
        // Use the position of the last call-to-action element
        // This is likely at the end of the content before recommended blogs
        const lastCtaElement = callToActionElements[callToActionElements.length - 1]
        const ctaPosition = lastCtaElement.getBoundingClientRect().top + window.scrollY
        
        // Set our content end position to be just after this element
        contentEndPosition = ctaPosition + (lastCtaElement as any).offsetHeight + 50 // add some padding
      } else {
        // If no call-to-action elements found, use a very large buffer (1000px) from recommended
        contentEndPosition = recommendedElement.getBoundingClientRect().top + window.scrollY - 1000
      }
      
      // The progress should be tracked only within the content area
      const startPosition = bannerBottom
      const endPosition = contentEndPosition
      const totalContentHeight = endPosition - startPosition
      
      // Calculate how far the user has scrolled through the actual content
      const scrolledInContent = Math.max(0, scrollPosition - startPosition)
      
      // Visibility conditions:
      // 1. Only show after passing most of the banner
      // 2. Hide when reaching our determined content end position
      const shouldBeVisible = 
        scrollPosition >= bannerBottom - 150 && 
        scrollPosition < contentEndPosition;
      
      setIsVisible(shouldBeVisible)
      
      // Calculate reading progress if visible
      if (shouldBeVisible) {
        // Ensure we reach 100% before hiding
        const readingPercentage = Math.min(100, Math.max(0, (scrolledInContent / totalContentHeight) * 100))
        setProgress(Math.round(readingPercentage))
      }
    }
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    
    // Initial calculation
    handleScroll()
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])
  
  // Use conditional rendering instead of opacity
  if (!isVisible) {
    return null;
  }
  
  return (
    <div className={classes.progressbarContainer}>
      <ProgressBar percentage={progress} />
    </div>
  )
}

export default ClientProgressBar